import { useRouteContext } from '@tanstack/react-router'

/**
 * Cached data belongs to one user working in one organisation. Every query key starts with that
 * scope, so switching organisation or signing in as someone else in the same tab can never show
 * the previous person's classes or exams.
 */
export function useScope(): string {
  const { session } = useRouteContext({ from: '/_authed' })
  return scopeOf(session)
}

export const scopeOf = (session: {
  user: { id: string }
  active: { organization: { id: string } } | null
}) => `${session.user.id}:${session.active?.organization.id ?? 'none'}`

export const classKeys = {
  all: (scope: string) => ['classes', scope] as const,
  list: (scope: string, archived: boolean) =>
    ['classes', scope, archived ? 'archived' : 'live'] as const,
  detail: (scope: string, id: string) => ['class', scope, id] as const,
  /** The grades of a class's exams, behind the grade history of a student. */
  results: (scope: string, id: string) => ['class-results', scope, id] as const,
  allResults: (scope: string) => ['class-results', scope] as const,
}

export const examKeys = {
  all: (scope: string) => ['exams', scope] as const,
  detail: (scope: string, id: string) => ['exam', scope, id] as const,
  submissions: (scope: string, id: string) => ['exam-submissions', scope, id] as const,
  guideDraft: (scope: string, id: string) => ['exam-guide-draft', scope, id] as const,
}

/** The focus areas of one exam or one class. */
export const insightKeys = {
  of: (scope: string, kind: 'exam' | 'class', id: string) => ['insight', scope, kind, id] as const,
}

export const gradingKeys = {
  overview: (scope: string, examId: string) => ['grading', scope, examId] as const,
  /** Under the overview's key, so whatever refreshes the overview refreshes the grade list too. */
  list: (scope: string, examId: string) => ['grading', scope, examId, 'grade-list'] as const,
  result: (scope: string, submissionId: string) => ['grade-result', scope, submissionId] as const,
  print: (scope: string, examId: string) => ['grading-print', scope, examId] as const,
}

export const billingKeys = {
  all: (scope: string) => ['billing', scope] as const,
  overview: (scope: string) => ['billing', scope, 'overview'] as const,
  invoices: (scope: string) => ['billing', scope, 'invoices'] as const,
  usage: (scope: string) => ['billing', scope, 'usage'] as const,
  credits: (scope: string) => ['billing', scope, 'credits'] as const,
}

/** The admin area. Everything is cached per signed-in admin, like the rest. */
export const adminKeys = {
  all: (scope: string) => ['admin', scope] as const,
  overview: (scope: string) => ['admin', scope, 'overview'] as const,
  stripe: (scope: string) => ['admin', scope, 'stripe'] as const,
  orgs: (scope: string, q: string, page: number) => ['admin', scope, 'orgs', q, page] as const,
  org: (scope: string, orgId: string) => ['admin', scope, 'org', orgId] as const,
  users: (scope: string, q: string, page: number) => ['admin', scope, 'users', q, page] as const,
  classes: (scope: string, q: string, page: number) =>
    ['admin', scope, 'classes', q, page] as const,
  accounts: (scope: string, q: string, status: string, page: number) =>
    ['admin', scope, 'accounts', q, status, page] as const,
  emails: (scope: string, q: string, page: number) => ['admin', scope, 'emails', q, page] as const,
  ai: (scope: string) => ['admin', scope, 'ai'] as const,
  events: (scope: string, filter: string) => ['admin', scope, 'events', filter] as const,
  beta: (scope: string, status: string) => ['admin', scope, 'beta', status] as const,
}

/** The signed-in user's own account page. */
export const accountKeys = {
  deletion: (scope: string) => ['account', scope, 'deletion'] as const,
  profile: (scope: string) => ['account', scope, 'profile'] as const,
}
